import { useState } from "react";
import { Environment, PRESET_ENVIRONMENTS } from "../lib/types";
import { getActiveEnvironment, setEnvironment, isTrustedBaseUrl } from "../lib/session";

// The "Local Rails :3000" preset only makes sense when the demo tool
// itself is running locally (npm run dev). On the hosted deploy we hide
// it so users don't pick an env that their browser can't reach.
const IS_LOCAL_HOST =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

const VISIBLE_PRESETS = PRESET_ENVIRONMENTS.filter(p => p.id !== "local" || IS_LOCAL_HOST);

export default function EnvSwitcher() {
  const [env, setEnv] = useState<Environment>(getActiveEnvironment());
  const [showCustom, setShowCustom] = useState(env.id === "custom");
  const [customUrl, setCustomUrl] = useState(env.id === "custom" ? env.baseUrl : "");

  function pickPreset(id: string) {
    if (id === "custom") {
      setShowCustom(true);
      return;
    }
    const picked = VISIBLE_PRESETS.find(e => e.id === id)!;
    setEnvironment(picked);
    setEnv(picked);
    setShowCustom(false);
  }

  function applyCustom() {
    if (!customUrl) return;
    if (!isTrustedBaseUrl(customUrl)) {
      const go = confirm(
        `"${customUrl}" is not a recognised HPAC host.\n\nYour API key will be sent to this URL as an Authorization header on every request. Continue only if you trust this host.`
      );
      if (!go) return;
    }
    const custom: Environment = { id: "custom", label: "Custom", baseUrl: customUrl, preset: false };
    setEnvironment(custom);
    setEnv(custom);
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      <label className="text-slate-500">Env</label>
      <select
        className="border border-slate-300 rounded px-2 py-1"
        value={env.id}
        onChange={e => pickPreset(e.target.value)}
      >
        {VISIBLE_PRESETS.map(p => (
          <option key={p.id} value={p.id}>{p.label}</option>
        ))}
        {IS_LOCAL_HOST && <option value="custom">Custom URL…</option>}
      </select>

      {showCustom && (
        <div className="flex items-center gap-1">
          <input
            className="border border-slate-300 rounded px-2 py-1 w-60 mono text-xs"
            placeholder="https://example.com"
            value={customUrl}
            onChange={e => setCustomUrl(e.target.value)}
          />
          <button className="text-xs bg-brand-600 text-white rounded px-2 py-1" onClick={applyCustom}>
            Set
          </button>
        </div>
      )}

      <span className="mono text-xs text-slate-400">{env.baseUrl}</span>
    </div>
  );
}
